import React, { useState } from "react";
import { Download, X, Wand2, ChevronDown, ChevronUp, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Lightbox modal for a completed tool job.
 *   - Shows a Before/After slider (source vs result)
 *   - Download original + Download result
 *   - "Verder bewerken met AI" — load the result back as the source on the
 *     parent page so the user can iterate (called via `onContinueEdit`).
 *   - When the viewer is an admin AND the job has owner/prompt info, an
 *     expandable "Prompt & user" details panel is rendered below the header
 *     so the admin can audit/review what the makelaar generated and how.
 *
 * Designed to be re-used by both AdminToolPhotoRetouch and AdminToolUpscale.
 */
export default function ToolJobModal({ job, onClose, onContinueEdit }) {
    const { t, i18n } = useTranslation();
    const { user: viewer } = useAuth();
    const isAdmin = viewer?.role === "admin";
    const [showDetails, setShowDetails] = useState(false);

    if (!job || !job.result_url || !job.source_url) return null;

    const shortId = job.id ? job.id.slice(0, 8) : "result";
    const sourceFull = job.source_url.startsWith("http") ? job.source_url : `${BACKEND}${job.source_url}`;
    const resultFull = job.result_url.startsWith("http") ? job.result_url : `${BACKEND}${job.result_url}`;

    const downloadUrl = (url, filename) => {
        const sep = url.includes("?") ? "&" : "?";
        return `${url}${sep}dl=${encodeURIComponent(filename)}`;
    };

    const meta = [];
    if (job.tool_key === "photo_retouch") {
        if (job.action_preset) meta.push(`${t("tools.modal.action")}: ${t(`tools.retouch.actions.${job.action_preset}`, job.action_preset)}`);
    } else if (job.tool_key === "upscale") {
        meta.push("Upscale");
    }
    if (job.created_at) {
        const dateLocale = { nl: "nl-BE", fr: "fr-BE", en: "en-GB" }[(i18n.language || "nl").slice(0, 2)] || "nl-BE";
        meta.push(new Date(job.created_at).toLocaleString(dateLocale, { dateStyle: "short", timeStyle: "short" }));
    }

    // Admin-only context: prompt + owner. We hide this entirely from regular
    // users — the prompt is internal data they don't need to see.
    const hasAdminContext = isAdmin && (job.prompt || job.user_name || job.user_email || job.comment_nl);

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
            onClick={onClose}
            data-testid="tool-job-modal"
        >
            <div
                className="relative w-full max-w-[1280px] max-h-[92vh] bg-white border border-zinc-200 rounded-sm flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
                    <div className="min-w-0 flex-1">
                        <p className="text-[11px] uppercase tracking-wider text-zinc-500">{t("tools.modal.result")}</p>
                        <p className="text-sm text-zinc-950 tabular-nums truncate">{meta.join(" · ")}</p>
                        {isAdmin && (job.user_name || job.user_email) && (
                            <p className="text-[11px] text-zinc-500 mt-0.5 inline-flex items-center gap-1.5" data-testid="tool-job-owner">
                                <User className="h-3 w-3 text-[#E5A93D]" />
                                {job.user_name || job.user_email}
                                {job.user_company && <span className="text-zinc-500">· {job.user_company}</span>}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="tool-job-modal-close"
                        className="h-8 w-8 flex items-center justify-center rounded-sm text-zinc-500 hover:bg-zinc-100 hover:text-zinc-950"
                        aria-label="Sluiten"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Admin-only details panel (collapsed by default to not steal slider real estate) */}
                {hasAdminContext && (
                    <div className="border-b border-zinc-200 bg-white">
                        <button
                            type="button"
                            onClick={() => setShowDetails((v) => !v)}
                            data-testid="tool-job-toggle-prompt"
                            className="w-full flex items-center justify-between px-5 py-2.5 text-[11px] uppercase tracking-wider text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition"
                        >
                            <span>Prompt &amp; details {job.fal_endpoint ? `· ${job.fal_endpoint.split("/").pop()}` : ""}</span>
                            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        {showDetails && (
                            <div className="px-5 pb-4 pt-1 space-y-3" data-testid="tool-job-prompt-panel">
                                {job.comment_nl && (
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Instructie (NL)</p>
                                        <p className="text-xs text-zinc-800 leading-relaxed whitespace-pre-wrap">{job.comment_nl}</p>
                                    </div>
                                )}
                                {job.prompt && (
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Prompt naar AI</p>
                                        <p className="text-xs text-zinc-600 leading-relaxed whitespace-pre-wrap font-mono" data-testid="tool-job-prompt-text">{job.prompt}</p>
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-zinc-500 pt-1 border-t border-zinc-100">
                                    {job.model && <span>Model: <span className="text-zinc-600">{job.model}</span></span>}
                                    {typeof job.cost_eur === "number" && <span>Kost: <span className="text-zinc-600">€{job.cost_eur.toFixed(3)}</span></span>}
                                    {typeof job.duration_ms === "number" && <span>Duur: <span className="text-zinc-600">{(job.duration_ms / 1000).toFixed(1)}s</span></span>}
                                    {job.credits_spent && <span>Credits: <span className="text-zinc-600">{job.credits_spent}</span></span>}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Slider */}
                <div className="flex-1 min-h-0 p-4 overflow-hidden bg-zinc-50 flex items-center justify-center">
                    <BeforeAfterSlider beforeUrl={sourceFull} afterUrl={resultFull} className="max-h-[68vh]" />
                </div>

                {/* Action bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t border-zinc-200">
                    <div className="flex flex-wrap items-center gap-2">
                        <a
                            href={downloadUrl(sourceFull, `origineel_${shortId}.jpg`)}
                            download={`origineel_${shortId}.jpg`}
                            data-testid="tool-job-download-original"
                            className="inline-flex items-center gap-1.5 h-9 px-3 text-xs rounded-sm border border-zinc-200 bg-white hover:bg-zinc-100 text-zinc-950 transition"
                        >
                            <Download className="h-3.5 w-3.5" /> {t("tools.modal.download_original")}
                        </a>
                        <a
                            href={downloadUrl(resultFull, `ai_resultaat_${shortId}.jpg`)}
                            download={`ai_resultaat_${shortId}.jpg`}
                            data-testid="tool-job-download-result"
                            className="inline-flex items-center gap-1.5 h-9 px-3 text-xs rounded-sm bg-white text-zinc-950 hover:bg-zinc-100 font-medium transition"
                        >
                            <Download className="h-3.5 w-3.5" /> {t("tools.modal.download_result")}
                        </a>
                    </div>
                    {onContinueEdit && (
                        <Button
                            onClick={() => onContinueEdit(job)}
                            data-testid="tool-job-continue-edit"
                            className="bg-[#E5A93D] hover:bg-[#E5A93D]/90 text-black font-medium rounded-sm h-9 px-4 text-xs"
                        >
                            <Wand2 className="h-3.5 w-3.5 mr-1.5" /> {t("tools.modal.continue_edit")}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
